import { useState } from "react";
import NewsCard from "../NewsCard";
import Meta from "../common/Meta";

const TIER_LIMIT = 5;

function sortByRecency(articles) {
  return [...articles].sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
}

function splitByTier(articles) {
  return {
    gun:       articles.filter(a => a.cls === "gun"),
    watershed: articles.filter(a => a.cls === "watershed"),
    notable:   articles.filter(a => a.cls === "notable"),
    common:    articles.filter(a => !a.cls || a.cls === "common"),
  };
}

function TierSection({ articles, onSelectArticle, onOverride, showDivider = true }) {
  const [visibleCount, setVisibleCount] = useState(TIER_LIMIT);
  if (!articles || articles.length === 0) return null;

  const sorted = sortByRecency(articles);
  const visible = sorted.slice(0, visibleCount);
  const remaining = sorted.length - visibleCount;

  return (
    <div>
      {showDivider && (
        <div className="px-4 mt-1">
          <div className="border-t-2 border-white/60" />
        </div>
      )}
      {visible.map(n => (
        <NewsCard key={n.id} article={n} onClick={onSelectArticle} onOverride={onOverride} />
      ))}
      {remaining > 0 && (
        <div className="px-4 pt-1 pb-4 text-center">
          <button
            onClick={() => setVisibleCount(v => v + TIER_LIMIT)}
            className="bg-transparent border-none text-text-secondary text-[13px] font-semibold cursor-pointer py-1 w-full"
          >
            더 보기
          </button>
        </div>
      )}
    </div>
  );
}

function TieredList({ articles, onSelectArticle, onOverride }) {
  if (!articles || articles.length === 0) return null;
  const byTier = splitByTier(articles);
  return (
    <div>
      <TierSection
        articles={byTier.gun}
        onSelectArticle={onSelectArticle}
        onOverride={onOverride}
        showDivider={false}
      />
      <TierSection
        articles={byTier.watershed}
        onSelectArticle={onSelectArticle}
        onOverride={onOverride}
        showDivider={byTier.gun.length > 0}
      />
      <TierSection
        articles={byTier.notable}
        onSelectArticle={onSelectArticle}
        onOverride={onOverride}
      />
      <TierSection
        articles={byTier.common}
        onSelectArticle={onSelectArticle}
        onOverride={onOverride}
      />
    </div>
  );
}

function findSourceUrl(bullet, sources) {
  if (!sources?.length) return null;
  for (const s of sources) {
    if (bullet.includes(s.src)) return s.url;
  }
  return null;
}

function AdversaryInsightPanel({ frame }) {
  if (!frame?.insights?.length) return null;
  return (
    <div className="mx-4 my-3 border border-border-line rounded p-3 bg-bg-secondary">
      <div className="mb-3">
        <span className="text-meta font-bold tracking-widest uppercase text-text-tertiary">이란·중국·러시아 미디어 인사이트</span>
      </div>
      <div className="flex flex-col gap-3">
        {frame.insights.map((insight, i) => (
          <div key={i}>
            <div className="text-sub font-bold text-text-primary mb-1">{insight.keyword}</div>
            <div className="flex flex-col gap-1">
              {(insight.bullets || []).map((b, j) => {
                const url = findSourceUrl(b, frame.sources);
                return (
                  <div
                    key={j}
                    className={`flex gap-2 items-start ${url ? "cursor-pointer hover:text-text-primary" : ""}`}
                    onClick={url ? () => window.open(url, "_blank", "noopener,noreferrer") : undefined}
                  >
                    <span className="text-text-tertiary text-sub flex-shrink-0 mt-[2px]">·</span>
                    <span className="text-sub text-text-secondary leading-snug">{b}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function UnverifiedTab({ socmint, adversary, adversaryFrame, unverifiedPanel, setUnverifiedPanel, onSelectArticle, onOverride }) {
  return (
    <div>
      {/* Sub-tabs */}
      <div className="flex border-b border-border-line">
        {[["socmint", "Rumor"], ["adversary", "Adversary News"]].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setUnverifiedPanel(id)}
            className={`flex-1 min-h-[44px] py-2.5 bg-transparent border-none cursor-pointer
              text-sub tracking-widest uppercase transition-colors
              ${unverifiedPanel === id
                ? "font-bold text-text-primary border-b-2 border-text-primary"
                : "font-normal text-text-tertiary border-b-2 border-transparent"
              }`}
          >
            {label}
          </button>
        ))}
      </div>

      {unverifiedPanel === "socmint" && (
        <div>
          {socmint.length === 0 ? (
            <div className="py-8 px-4 text-center"><Meta>데이터 로딩 중…</Meta></div>
          ) : (
            <TieredList articles={socmint} onSelectArticle={onSelectArticle} onOverride={onOverride} />
          )}
        </div>
      )}

      {unverifiedPanel === "adversary" && (
        <div>
          <AdversaryInsightPanel frame={adversaryFrame} />
          {adversary.length === 0 ? (
            <div className="py-8 px-4 text-center"><Meta>데이터 로딩 중…</Meta></div>
          ) : (
            <TieredList articles={adversary} onSelectArticle={onSelectArticle} onOverride={onOverride} />
          )}
        </div>
      )}
    </div>
  );
}
